import { CSSProperties, useEffect, useState } from "react";
import { analyticsTheme } from "@/features/orders-analytics/theme/tokens";
import { OrdersAnalyticsFilters } from "@/features/orders-analytics/types/orders-analytics.types";
import { timezone } from "@/features/orders-analytics/utils/timezone";

type Variant = "default" | "diagnostic";

type Props = {
    filters: OrdersAnalyticsFilters;
    availableClients: string[];
    onFiltersChange: (next: OrdersAnalyticsFilters) => void;
    onRefresh: () => void;
    loading: boolean;
    variant?: Variant;
    // Opt OUT of the client selector on a dashboard whose endpoint is single-tenant by
    // design. Offering a choice the backend cannot honour is worse than offering none:
    // the page reloads and re-renders the SAME tenant's figures under another client's
    // name. Defaults to true — every multi-tenant consumer keeps its selector.
    showClientSelector?: boolean;
};

export function FiltersBar({
    filters,
    availableClients,
    onFiltersChange,
    onRefresh,
    loading,
    variant = "default",
    showClientSelector = true,
}: Props) {
    const isDiagnostic = variant === "diagnostic";
    const [isCustomDateModalOpen, setIsCustomDateModalOpen] = useState(false);
    const [customDateFromInput, setCustomDateFromInput] = useState(() =>
        timezone.toTimeZoneInputDateTime(filters.dateFrom)
    );
    const [customDateToInput, setCustomDateToInput] = useState(() =>
        timezone.toTimeZoneInputDateTime(filters.dateTo)
    );

    useEffect(() => {
        if (!isCustomDateModalOpen) {
            setCustomDateFromInput(timezone.toTimeZoneInputDateTime(filters.dateFrom));
            setCustomDateToInput(timezone.toTimeZoneInputDateTime(filters.dateTo));
        }
    }, [filters.dateFrom, filters.dateTo, isCustomDateModalOpen]);

    const selectedClient = filters.clients[0] ?? "";

    const applyLastFullMonth = () => {
        const range = timezone.getTimeZoneLastFullMonthRange(Date.now());
        onFiltersChange({
            ...filters,
            comparisonPeriod: "last_full_month",
            dateFrom: range.from,
            dateTo: range.to,
        });
    };

    const applyMonthToDate = () => {
        const now = Date.now();
        onFiltersChange({
            ...filters,
            comparisonPeriod: "month_to_date",
            dateFrom: timezone.getTimeZoneMonthStartTimestamp(now),
            dateTo: now,
        });
    };

    const openCustomDateModal = () => {
        setCustomDateFromInput(timezone.toTimeZoneInputDateTime(filters.dateFrom));
        setCustomDateToInput(timezone.toTimeZoneInputDateTime(filters.dateTo));
        setIsCustomDateModalOpen(true);
    };

    const closeCustomDateModal = () => {
        setIsCustomDateModalOpen(false);
        setCustomDateFromInput(timezone.toTimeZoneInputDateTime(filters.dateFrom));
        setCustomDateToInput(timezone.toTimeZoneInputDateTime(filters.dateTo));
    };

    const applyCustomDateRange = () => {
        const nextDateFrom = timezone.parseTimeZoneInputDateTime(customDateFromInput);
        const nextDateTo = timezone.parseTimeZoneInputDateTime(customDateToInput);
        if (nextDateFrom === null || nextDateTo === null || nextDateFrom > nextDateTo) {
            return;
        }
        onFiltersChange({
            ...filters,
            comparisonPeriod: "previous_range",
            dateFrom: nextDateFrom,
            dateTo: nextDateTo,
        });
        setIsCustomDateModalOpen(false);
    };

    const customDateModal = isCustomDateModalOpen ? (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="custom-date-range-title"
            style={modalOverlayStyle}
        >
            <div style={modalCardStyle}>
                <div>
                    <h2 id="custom-date-range-title" style={modalTitleStyle}>
                        Custom date range
                    </h2>
                    <p style={modalDescriptionStyle}>
                        Select a dashboard-timezone range. Queries run only after confirmation.
                    </p>
                </div>

                <div style={modalFieldsStyle}>
                    <label style={dateLabelStyle}>
                        Start date/time
                        <input
                            type="datetime-local"
                            value={customDateFromInput}
                            onChange={(event) => setCustomDateFromInput(event.target.value)}
                            style={dateInputStyle}
                        />
                    </label>

                    <label style={dateLabelStyle}>
                        End date/time
                        <input
                            type="datetime-local"
                            value={customDateToInput}
                            onChange={(event) => setCustomDateToInput(event.target.value)}
                            style={dateInputStyle}
                        />
                    </label>
                </div>

                <div style={modalActionsStyle}>
                    <button type="button" onClick={closeCustomDateModal} style={secondaryButtonStyle}>
                        Cancel
                    </button>
                    <button type="button" onClick={applyCustomDateRange} style={primaryButtonStyle}>
                        Apply custom range
                    </button>
                </div>
            </div>
        </div>
    ) : null;

    if (isDiagnostic) {
        return (
            <div className="filterbar">
                {showClientSelector ? (
                <label className="filter" style={diagnosticClientWrapStyle}>
                    <span aria-hidden className="material-symbols-outlined">
                        group
                    </span>
                    <span className="l">Client:</span>
                    <span style={visuallyHiddenStyle}>Client universe</span>
                    <select
                        aria-label="Client universe"
                        value={selectedClient}
                        onChange={(event) =>
                            onFiltersChange({
                                ...filters,
                                clients: event.target.value ? [event.target.value] : [],
                            })
                        }
                        style={diagnosticClientSelectStyle}
                    >
                        <option value="">All</option>
                        {selectedClient && !availableClients.includes(selectedClient) ? (
                            <option value={selectedClient}>{selectedClient}</option>
                        ) : null}
                        {availableClients.map((client) => (
                            <option key={client} value={client}>
                                {client}
                            </option>
                        ))}
                    </select>
                </label>
                ) : null}

                <button
                    type="button"
                    onClick={applyLastFullMonth}
                    className={
                        filters.comparisonPeriod === "last_full_month"
                            ? "filter filter--active"
                            : "filter"
                    }
                >
                    <span aria-hidden className="material-symbols-outlined">
                        event
                    </span>
                    <span className="l">Range:</span>
                    <span className="v">Last full month</span>
                </button>

                <button
                    type="button"
                    onClick={applyMonthToDate}
                    className={
                        filters.comparisonPeriod === "month_to_date"
                            ? "filter filter--active"
                            : "filter"
                    }
                >
                    <span aria-hidden className="material-symbols-outlined">
                        today
                    </span>
                    <span className="v">Month to date</span>
                </button>

                <button
                    type="button"
                    onClick={openCustomDateModal}
                    className={
                        filters.comparisonPeriod === "previous_range"
                            ? "filter filter--active"
                            : "filter"
                    }
                >
                    <span aria-hidden className="material-symbols-outlined">
                        date_range
                    </span>
                    <span className="v">Custom range</span>
                </button>

                <div className="filterbar__spacer" />

                <span className="filterbar__count">
                    Clients: <strong>{availableClients.length}</strong>
                </span>

                <button
                    type="button"
                    onClick={onRefresh}
                    disabled={loading}
                    className="btn btn--primary btn--sm"
                >
                    <span aria-hidden className="material-symbols-outlined">
                        {loading ? "progress_activity" : "refresh"}
                    </span>
                    <span>{loading ? "Refreshing…" : "Refresh"}</span>
                </button>

                {customDateModal}
            </div>
        );
    }

    return (
        <div style={barStyle}>
            {showClientSelector ? (
            <label style={clientSelectorLabelStyle}>
                <span style={visuallyHiddenStyle}>Client universe</span>
                <select
                    aria-label="Client universe"
                    value={selectedClient}
                    onChange={(event) =>
                        onFiltersChange({
                            ...filters,
                            clients: event.target.value ? [event.target.value] : [],
                        })
                    }
                    style={selectStyle}
                >
                    <option value="">All clients</option>
                    {selectedClient && !availableClients.includes(selectedClient) ? (
                        <option value={selectedClient}>{selectedClient}</option>
                    ) : null}
                    {availableClients.map((client) => (
                        <option key={client} value={client}>
                            {client}
                        </option>
                    ))}
                </select>
            </label>
            ) : null}

            <button
                type="button"
                onClick={applyLastFullMonth}
                style={buildPeriodButtonStyle(filters.comparisonPeriod === "last_full_month")}
            >
                Last full month
            </button>

            <button
                type="button"
                onClick={applyMonthToDate}
                style={buildPeriodButtonStyle(filters.comparisonPeriod === "month_to_date")}
            >
                Month to date
            </button>

            <button
                type="button"
                onClick={openCustomDateModal}
                style={buildPeriodButtonStyle(filters.comparisonPeriod === "previous_range")}
            >
                Custom date
            </button>

            <button onClick={onRefresh} disabled={loading} style={buildRefreshButtonStyle(loading)}>
                {loading ? "Refreshing..." : "Refresh"}
            </button>

            {customDateModal}
        </div>
    );
}

const diagnosticClientWrapStyle: CSSProperties = {
    padding: "0 8px 0 10px",
};

const diagnosticClientSelectStyle: CSSProperties = {
    border: 0,
    background: "transparent",
    color: "inherit",
    font: "inherit",
    fontWeight: 600,
    padding: 0,
    outline: "none",
    cursor: "pointer",
    minWidth: 0,
    maxWidth: 160,
    appearance: "none",
    paddingRight: 14,
    backgroundImage:
        "linear-gradient(45deg, transparent 50%, currentColor 50%), linear-gradient(135deg, currentColor 50%, transparent 50%)",
    backgroundPosition: "calc(100% - 6px) 50%, calc(100% - 3px) 50%",
    backgroundSize: "4px 4px, 4px 4px",
    backgroundRepeat: "no-repeat",
    opacity: 0.95,
};

const barStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "clamp(0.4rem, 0.35vw + 0.2rem, 0.6rem)",
    flexWrap: "wrap",
    minWidth: 0,
};

const buildPeriodButtonStyle = (active: boolean): CSSProperties => ({
    border: `1px solid ${active ? analyticsTheme.colors.primary : analyticsTheme.colors.borderStrong}`,
    borderRadius: 999,
    background: active ? analyticsTheme.colors.primary : analyticsTheme.colors.cardBg,
    color: active ? analyticsTheme.colors.cardBg : analyticsTheme.colors.textSecondary,
    fontWeight: 600,
    fontSize: "clamp(0.7rem, 0.1vw + 0.66rem, 0.78rem)",
    lineHeight: 1.2,
    padding: "clamp(0.32rem, 0.18vw + 0.22rem, 0.45rem) clamp(0.65rem, 0.3vw + 0.45rem, 0.9rem)",
    minHeight: "clamp(1.75rem, 1.5vw + 1rem, 2rem)",
    cursor: "pointer",
    whiteSpace: "nowrap",
});

const buildRefreshButtonStyle = (loading: boolean): CSSProperties => ({
    border: "none",
    borderRadius: 999,
    padding: "clamp(0.36rem, 0.22vw + 0.24rem, 0.5rem) clamp(0.8rem, 0.36vw + 0.55rem, 1rem)",
    minHeight: "clamp(1.75rem, 1.5vw + 1rem, 2rem)",
    background: loading ? analyticsTheme.colors.borderStrong : "#1A2128",
    color: "#FFFFFF",
    fontWeight: 600,
    fontSize: "clamp(0.7rem, 0.1vw + 0.66rem, 0.78rem)",
    lineHeight: 1.2,
    cursor: loading ? "not-allowed" : "pointer",
    whiteSpace: "nowrap",
});

const selectStyle: CSSProperties = {
    border: `1px solid ${analyticsTheme.colors.borderStrong}`,
    borderRadius: 999,
    background: analyticsTheme.colors.cardBg,
    color: analyticsTheme.colors.textPrimary,
    fontWeight: 600,
    fontSize: "clamp(0.7rem, 0.1vw + 0.66rem, 0.78rem)",
    lineHeight: 1.2,
    padding: "clamp(0.32rem, 0.18vw + 0.22rem, 0.45rem) clamp(1.6rem, 0.6vw + 1.2rem, 2rem) clamp(0.32rem, 0.18vw + 0.22rem, 0.45rem) clamp(0.65rem, 0.3vw + 0.45rem, 0.9rem)",
    minHeight: "clamp(1.75rem, 1.5vw + 1rem, 2rem)",
    maxWidth: "12rem",
    minWidth: "8rem",
    outline: "none",
};

const clientSelectorLabelStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
};

const secondaryButtonStyle: CSSProperties = {
    border: `1px solid ${analyticsTheme.colors.borderStrong}`,
    borderRadius: 999,
    background: analyticsTheme.colors.cardBg,
    color: analyticsTheme.colors.textSecondary,
    fontWeight: 600,
    fontSize: 13,
    padding: "8px 14px",
    cursor: "pointer",
};

const primaryButtonStyle: CSSProperties = {
    border: "none",
    borderRadius: 999,
    background: analyticsTheme.colors.primary,
    color: analyticsTheme.colors.cardBg,
    fontWeight: 600,
    fontSize: 13,
    padding: "8px 16px",
    cursor: "pointer",
};

const dateLabelStyle: CSSProperties = {
    display: "grid",
    gap: 6,
    color: analyticsTheme.colors.textSecondary,
    fontSize: 12,
    fontWeight: 600,
};

const dateInputStyle: CSSProperties = {
    border: `1px solid ${analyticsTheme.colors.border}`,
    borderRadius: 6,
    background: analyticsTheme.colors.cardBg,
    color: analyticsTheme.colors.textPrimary,
    padding: "8px 10px",
    fontSize: 13,
    outline: "none",
};

const modalOverlayStyle: CSSProperties = {
    position: "fixed",
    inset: 0,
    zIndex: 50,
    display: "grid",
    placeItems: "center",
    padding: 16,
    background: "rgba(15, 23, 42, 0.42)",
};

const modalCardStyle: CSSProperties = {
    width: "min(520px, 100%)",
    borderRadius: 12,
    background: analyticsTheme.colors.cardBg,
    border: `1px solid ${analyticsTheme.colors.border}`,
    boxShadow: analyticsTheme.shadows.soft,
    padding: 20,
    display: "grid",
    gap: 16,
};

const modalTitleStyle: CSSProperties = {
    margin: 0,
    color: analyticsTheme.colors.primary,
    fontSize: 20,
    fontWeight: 600,
};

const modalDescriptionStyle: CSSProperties = {
    margin: "4px 0 0 0",
    color: analyticsTheme.colors.textMuted,
    fontSize: 13,
};

const modalFieldsStyle: CSSProperties = {
    display: "grid",
    gap: 12,
};

const modalActionsStyle: CSSProperties = {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    flexWrap: "wrap",
};

const visuallyHiddenStyle: CSSProperties = {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: "1px",
    margin: "-1px",
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    whiteSpace: "nowrap",
    width: "1px",
};
